/**
 * 第一章「薪火初燃」5 关配置
 *
 * 设计文档（唯一事实来源）：`docs/传灯-第一章关卡设计表.md`
 * 节奏：安全起飞（L1）→ 引入复杂度（L2）→ 首次抉择（L3）→ 压力峰值（L4）→ 综合收尾（L5）。
 *
 * 铁律：这里**只放数据**。客户端与服务端都用 `buildMatchSetup()` 由本文件构造
 * MatchSetup，保证复算校验两端输入完全一致。
 */

import type { ChapterConfig, LevelConfig } from "../types.js";

const LIFE_ROOT = "life-root";

const L1: LevelConfig = {
  id: "c1-l1",
  chapterId: "c1",
  index: 1,
  title: "薪火初燃",
  teach: "学会传播与结束回合：一条路、一个目标，怎么走都不会输。",
  mapId: "world-v1",
  regionIds: ["r-hearth", "r-riverland"],
  routeIds: ["rt-hearth-river"],
  startRegionId: "r-hearth",
  goalEnlightenment: 55,
  mistGrowth: 0,
  turnLimit: 10,
  eventChance: 0,
  eventPoolIds: [],
  skillNodeIds: [LIFE_ROOT],
  cardIds: ["biology-001"],
};

const L2: LevelConfig = {
  id: "c1-l2",
  chapterId: "c1",
  index: 2,
  title: "小径通幽",
  teach: "第一次花钱升级火种，第一次答题：答错也有收获，不会卡住你。",
  mapId: "world-v1",
  regionIds: ["r-hearth", "r-riverland", "r-pineforest"],
  routeIds: ["rt-hearth-river", "rt-river-pine", "rt-hearth-pine"],
  startRegionId: "r-hearth",
  goalEnlightenment: 72,
  mistGrowth: 2,
  turnLimit: 14,
  eventChance: 0.3,
  eventPoolIds: ["ev-traveling-scholar", "ev-merchant", "ev-bumper-harvest"],
  skillNodeIds: [LIFE_ROOT, "life-hygiene", "life-observe"],
  cardIds: ["biology-001", "biology-002", "biology-012", "biology-015"],
};

const L3: LevelConfig = {
  id: "c1-l3",
  chapterId: "c1",
  index: 3,
  title: "双途抉择",
  teach: "高原路贵、荒漠路便宜：同一条终点，选择不同的路。",
  mapId: "world-v1",
  regionIds: ["r-hearth", "r-riverland", "r-pineforest", "r-cloudridge", "r-saltflat"],
  routeIds: [
    "rt-hearth-river",
    "rt-river-pine",
    "rt-hearth-pine",
    "rt-pine-cloud",
    "rt-river-salt",
  ],
  startRegionId: "r-hearth",
  goalEnlightenment: 78,
  mistGrowth: 3,
  turnLimit: 16,
  eventChance: 0.35,
  eventPoolIds: [
    "ev-traveling-scholar",
    "ev-merchant",
    "ev-bumper-harvest",
    "ev-ancient-book",
    "ev-rumor",
  ],
  skillNodeIds: [
    LIFE_ROOT,
    "life-hygiene",
    "life-observe",
    "life-nutrition",
    "life-quarantine",
    "life-vectors",
  ],
  cardIds: [
    "biology-001",
    "biology-002",
    "biology-003",
    "biology-005",
    "biology-010",
    "biology-012",
    "biology-015",
    "geography-001",
  ],
};

const L4: LevelConfig = {
  id: "c1-l4",
  chapterId: "c1",
  index: 4,
  title: "迷雾反扑",
  teach: "迷雾每回合都会增长，商路随时可能断绝：用抗性和地图知识顶住。",
  mapId: "world-v1",
  regionIds: [
    "r-hearth",
    "r-riverland",
    "r-pineforest",
    "r-cloudridge",
    "r-saltflat",
    "r-stonegate",
  ],
  routeIds: [
    "rt-hearth-river",
    "rt-river-pine",
    "rt-hearth-pine",
    "rt-pine-cloud",
    "rt-river-salt",
    "rt-cloud-stone",
  ],
  startRegionId: "r-hearth",
  goalEnlightenment: 88,
  mistGrowth: 4,
  turnLimit: 18,
  eventChance: 0.4,
  eventPoolIds: [
    "ev-traveling-scholar",
    "ev-merchant",
    "ev-bumper-harvest",
    "ev-ancient-book",
    "ev-rumor",
    "ev-mist-surge",
    "ev-close-route",
    "ev-plague",
    "ev-disaster",
  ],
  skillNodeIds: [
    LIFE_ROOT,
    "life-hygiene",
    "life-observe",
    "life-nutrition",
    "life-quarantine",
    "life-vectors",
    "life-vaccine",
    "life-epimap",
    "life-stats",
    "life-disinfect",
  ],
  cardIds: [
    "biology-001",
    "biology-002",
    "biology-003",
    "biology-004",
    "biology-005",
    "biology-007",
    "biology-010",
    "biology-012",
    "biology-013",
    "biology-015",
    "math-001",
    "biology-016",
    "geography-001",
  ],
};

const L5: LevelConfig = {
  id: "c1-l5",
  chapterId: "c1",
  index: 5,
  title: "薪火相传",
  teach: "整块大陆：陆路全点亮，再用地图知识找到那条藏起来的海路。",
  mapId: "world-v1",
  regionIds: [
    "r-hearth",
    "r-riverland",
    "r-pineforest",
    "r-cloudridge",
    "r-saltflat",
    "r-stonegate",
    "r-longcoast",
    "r-lagoonisle",
  ],
  routeIds: [
    "rt-hearth-river",
    "rt-river-pine",
    "rt-hearth-pine",
    "rt-pine-cloud",
    "rt-river-salt",
    "rt-cloud-stone",
    "rt-stone-long",
    "rt-long-salt",
    // 唯一的海路：默认隐藏，靠「疫病地图」节点显现
    "rt-sea-long-lagoon",
  ],
  startRegionId: "r-hearth",
  goalEnlightenment: 90,
  mistGrowth: 6,
  turnLimit: 22,
  eventChance: 0.4,
  eventPoolIds: [
    "ev-traveling-scholar",
    "ev-merchant",
    "ev-bumper-harvest",
    "ev-ancient-book",
    "ev-rumor",
    "ev-mist-surge",
    "ev-close-route",
    "ev-plague",
    "ev-disaster",
  ],
  skillNodeIds: [
    LIFE_ROOT,
    "life-hygiene",
    "life-observe",
    "life-nutrition",
    "life-quarantine",
    "life-vectors",
    "life-vaccine",
    "life-epimap",
    "life-stats",
    "life-disinfect",
    "life-ecology",
    "life-immunity",
    "life-herd",
    "life-eradicate",
    "life-torchlight",
  ],
  cardIds: [
    // 技能树绑定的 15 张
    "biology-001",
    "biology-002",
    "biology-003",
    "biology-004",
    "biology-005",
    "biology-006",
    "biology-007",
    "biology-008",
    "biology-009",
    "biology-010",
    "biology-011",
    "biology-012",
    "biology-013",
    "biology-014",
    "math-001",
    // 事件考验的补充题源
    "biology-015",
    "biology-016",
    "biology-017",
    "biology-018",
    "geography-001",
    "geography-002",
  ],
};

export const CHAPTER1: ChapterConfig = {
  id: "c1",
  title: "薪火初燃",
  theme: "卫生与健康 · 传播机制入门",
  description:
    "火种第一次被点亮。玩家在炉火原学会传播、升级与答题，最终走遍整块大陆，并发现那条藏起来的海路。",
  levels: [L1, L2, L3, L4, L5],
};
